import React, { useMemo } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { STACK_PALETTE } from "../../cost-analyzer/AnalyzerChart.jsx";
import { BUCKET_LABELS, metricById } from "./trendsConfig";
import { compactNumber } from "../format";
import { humanEUR } from "../../cost-control/forecast/format";
import IdleCapacityNote from "./IdleCapacityNote";

const CHART_H = 320;
const OTHER = "hsl(var(--chart-axis))";

// Past this many layers the palette repeats and the stack stops being
// readable; the remainder folds into one "Other" layer held back in grey so a
// real category never reads as miscellaneous. Same cap, same treatment, as
// AnalyzerChart — the two analysis pages are meant to read as one system.
// The endpoint never emits a literal "Other" group (see the breakdown columns
// in cmd/api/ai_trends_query.go), so the folding is the client's to do and
// there is nothing to collide with.
export const STACK_CAP = 5;
export const OTHER_GROUP = "Other";

// Stacked COLUMNS, not the filled area Cost & Usage uses. Token volumes swing
// by orders of magnitude and columns keep individual periods and spikes
// readable where a filled area smears them together.

// toStackedRows pivots the flat (bucket, group) cells the endpoint returns into
// one row per bucket with a key per group. Absent groups are filled with 0 so
// the stack stays aligned and recharts does not drop a segment mid-series.
export function toStackedRows(series, metricId, cap = STACK_CAP) {
  const buckets = [];
  const seen = new Set();
  const total = new Map();
  const cell = new Map();

  for (const p of series || []) {
    if (!seen.has(p.bucket)) {
      seen.add(p.bucket);
      buckets.push(p.bucket);
    }
    const v = p[metricId] || 0;
    total.set(p.group, (total.get(p.group) || 0) + v);
    cell.set(`${p.bucket}|${p.group}`, v);
  }

  // Ranked by size so the cap keeps the layers worth looking at, with a name
  // tiebreak so equal groups (all-zero rows before a backfill, say) do not
  // reorder between two identical renders.
  const ranked = [...total.entries()]
    .sort((a, b) => (Math.abs(b[1]) - Math.abs(a[1])) || String(a[0]).localeCompare(String(b[0])))
    .map(([g]) => g);
  const kept = ranked.slice(0, cap);
  const folded = ranked.slice(cap);
  const groups = folded.length ? [...kept, OTHER_GROUP] : kept;

  const rows = buckets.map((bucket) => {
    const row = { bucket };
    // Every layer gets an explicit 0 rather than being absent: recharts drops
    // an absent segment mid-series and the stack stops lining up.
    for (const g of kept) row[g] = cell.get(`${bucket}|${g}`) || 0;
    if (folded.length) {
      row[OTHER_GROUP] = folded.reduce((sum, g) => sum + (cell.get(`${bucket}|${g}`) || 0), 0);
    }
    return row;
  });
  return { rows, groups };
}

function formatValue(v, kind) {
  if (kind === "money") return humanEUR(v);
  return compactNumber(v);
}

// ConsumptionTooltip mirrors AnalyzerTooltip's stacked branch (per-group rows with a
// colour swatch and share of the bucket, then a bold Total) using theme tokens rather
// than recharts' default contentStyle, which is hardcoded light and reads as broken in
// dark mode.
function ConsumptionTooltip({ active, payload, label, kind }) {
  if (!active || !payload?.length) return null;
  const rows = payload
    .filter((p) => p.value != null)
    .map((p) => ({ key: p.dataKey, label: p.name ?? p.dataKey, value: Number(p.value) || 0, color: p.color }));
  if (rows.length === 0) return null;
  const total = rows.reduce((s, r) => s + r.value, 0);
  return (
    <div className="rounded-card border border-card-bd bg-card px-3.5 py-3">
      <div className="mb-1.5 text-sm font-semibold text-text-2">{label}</div>
      {rows.map((r) => (
        <div key={r.key} className="flex items-center justify-between gap-4 text-xs text-text-2">
          <span className="flex items-center gap-1.5">
            <span className="inline-block shrink-0 rounded-full" style={{ width: 8, height: 8, backgroundColor: r.color }} />
            {r.label}
          </span>
          <span className="tabular-nums text-text">
            {formatValue(r.value, kind)}
            {total > 0 && <span className="ml-1.5 text-text-3">{Math.round((100 * r.value) / total)}%</span>}
          </span>
        </div>
      ))}
      <div className="mt-1 flex items-center justify-between gap-4 border-t border-card-bd pt-1 text-xs font-semibold text-text">
        <span>Total</span>
        <span className="tabular-nums">{formatValue(total, kind)}</span>
      </div>
    </div>
  );
}

export default function ConsumptionChart({
  series, metric, breakdownLabel, bucket, ownership, idleCapacity,
}) {
  const m = metricById(metric);
  const { rows, groups } = useMemo(() => toStackedRows(series, metric), [series, metric]);

  // Ownership perspectives are ALLOCATED from allocation_result_daily, a daily
  // allocation with no per-request rows, so there is no honest per-owner event
  // count and the endpoint returns 0 for every point (see aiTrendsByOwner).
  // Plotting that would draw an empty chart, which reads as "no AI requests in
  // this period" — a wrong answer stated confidently. Say what is true
  // instead, and say what to do about it.
  const eventsUnavailable = metric === "events" && ownership;

  return (
    <div className="rounded-card border border-card-bd bg-card p-4">
      <div className="flex items-baseline justify-between">
        <div className="text-[15px] font-semibold text-text">AI consumption by {breakdownLabel}</div>
        <div className="text-[12px] text-text-3">
          {BUCKET_LABELS[bucket] || "Daily"} · {m.label}
          {ownership ? " · Allocated by the rule that assigns cost" : ""}
        </div>
      </div>

      {eventsUnavailable ? (
        <div className="py-16 text-center text-[13px] text-text-3" style={{ maxWidth: 560, marginInline: "auto" }}>
          Request counts are not available for ownership breakdowns: ownership figures are
          allocated from daily cost, which carries no per-request detail. Switch the metric to
          tokens or cost, or break down by provider, model or API key to see events.
        </div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center text-[13px] text-text-3">
          No AI consumption in this period.
        </div>
      ) : (
        <div style={{ height: CHART_H }} className="mt-3">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--chart-grid))" vertical={false} />
              <XAxis dataKey="bucket" tick={{ fontSize: 11 }} stroke="hsl(var(--chart-axis))" />
              <YAxis
                tick={{ fontSize: 11 }}
                stroke="hsl(var(--chart-axis))"
                tickFormatter={(v) => formatValue(v, m.kind)}
              />
              <Tooltip
                content={<ConsumptionTooltip kind={m.kind} />}
                cursor={{ fill: "hsl(var(--chart-grid))", fillOpacity: 0.3 }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {groups.map((g, i) => (
                <Bar
                  key={g}
                  dataKey={g}
                  stackId="consumption"
                  fill={g === "Other" ? OTHER : STACK_PALETTE[i % STACK_PALETTE.length]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* The `actual` metric is the sum of the breakdown rows, so it excludes
          idle capacity while the Actual KPI above includes it. */}
      {m.id === "actual" && <IdleCapacityNote idleCapacity={idleCapacity} className="mt-2" />}
    </div>
  );
}
